import { SxProps, Theme } from "@mui/material";
import { ReactNode } from "react";
import Flex from "@/components/Flex";
import OutlineCardV2 from "@/components/OutlineCardV2";
import Text from "@/components/Text";
import {
  CHART_COLORS,
  CHART_TYPOGRAPHY,
} from "@/components/app/provider/patient/chart";

export type DetailCardProps = {
  title?: string;
  /** "card" is a plain heading; "field" is the uppercase caption style. */
  titleVariant?: "card" | "field";
  required?: boolean;
  action?: ReactNode;
  children: ReactNode;
  bgcolor?: string;
  sx?: SxProps<Theme>;
};

/**
 * Bordered card with a plain title row, used across the Group Details tabs.
 *
 * @author Rajkumar Rathod
 */
const DetailCard = ({
  title,
  titleVariant = "card",
  required,
  action,
  children,
  bgcolor,
  sx,
}: DetailCardProps) => {
  return (
    <OutlineCardV2 sx={{ p: 2, height: "100%", bgcolor, ...(sx as object) }}>
      <Flex flexDirection="column" gap={1.5} height="100%">
        {(!!title || !!action) && (
          <Flex alignItems="center" justifyContent="space-between" gap={1}>
            {!!title &&
              (titleVariant === "field" ? (
                <Text
                  {...CHART_TYPOGRAPHY.body5}
                  c={CHART_COLORS.textMutedGrey}
                  textTransform="uppercase"
                  letterSpacing="0.04em"
                >
                  {title}
                  {required && (
                    <Text component="span" c="error.main">
                      {" *"}
                    </Text>
                  )}
                </Text>
              ) : (
                <Text
                  {...CHART_TYPOGRAPHY.title6}
                  fontWeight={CHART_TYPOGRAPHY.weight.medium}
                  c={CHART_COLORS.textDarker}
                >
                  {title}
                </Text>
              ))}
            {action}
          </Flex>
        )}
        {children}
      </Flex>
    </OutlineCardV2>
  );
};

export default DetailCard;
